/**
 * Where a notification goes now that the app has no bell (F-0744, `docs/concept-notifications.md`
 * §No bell). Jay, 2026-10-28: *"we use decisions and tasks for me instead"*.
 *
 * A notice that asks for an answer is a **decision**, one that asks the person to do something is a
 * **task**, one about an agent's session is the **agents** view's (I-2382), and one that only reports
 * is the **record**: the item's journal, Activity or the access trail already shows it, so the app
 * does not draw it. Typed over every kind, so a new kind does not compile until it has a place.
 *
 * ⛔ This is the one table. The web lists, their badges and the server's `types` filter all read it.
 */

import { isCriticalNotification } from './notification-severity.js';
import { NOTIFICATION_TYPES, type NotificationType } from './types/index.js';

export const NOTICE_DESTINATIONS = ['decision', 'task', 'agents', 'record'] as const;
export type NoticeDestinationType = (typeof NOTICE_DESTINATIONS)[number];
/** A place that draws notices: every destination but the record. */
export type NoticePlaceType = Exclude<NoticeDestinationType, 'record'>;
export const NOTICE_PLACES: readonly NoticePlaceType[] = ['decision', 'task', 'agents'];

export const NOTIFICATION_DESTINATIONS: Record<NotificationType, NoticeDestinationType> = {
  [NOTIFICATION_TYPES.ACCESS_REQUESTED]: 'decision',
  [NOTIFICATION_TYPES.AGENT_CREATION_REQUESTED]: 'decision',
  [NOTIFICATION_TYPES.MOVE_REQUESTED]: 'task',
  [NOTIFICATION_TYPES.MENTIONED]: 'task',
  [NOTIFICATION_TYPES.RESPONSIBLE_CHANGED]: 'task',
  [NOTIFICATION_TYPES.COMMENT_ADDED]: 'task',
  [NOTIFICATION_TYPES.WIP_OVERRIDE_LAPSED]: 'task',
  [NOTIFICATION_TYPES.FLOW_NOTICE]: 'task',
  [NOTIFICATION_TYPES.OWNER_FLOW_NOTICE]: 'task',
  [NOTIFICATION_TYPES.HOLDER_STALE]: 'task',
  [NOTIFICATION_TYPES.BUDGET_WARNED]: 'task',
  [NOTIFICATION_TYPES.TASK_URGENT]: 'task',
  [NOTIFICATION_TYPES.MACHINE_IDENTITY_MISMATCH]: 'task',
  // F-0794 S8e: the owner changes the refused component back, or upgrades by hand.
  [NOTIFICATION_TYPES.PACKAGE_UPGRADE_REFUSED]: 'task',
  // MP-52c: a start somebody else made on the reader's machine ran; it opens the machine.
  [NOTIFICATION_TYPES.MACHINE_START_RAN]: 'task',
  // MP-109d.9: one of the reader's devices was unpaired from a machine; it opens the machine.
  [NOTIFICATION_TYPES.MACHINE_DEVICE_UNPAIRED]: 'task',
  [NOTIFICATION_TYPES.ACCESS_ENDING]: 'task',
  [NOTIFICATION_TYPES.ACCESS_ENDED]: 'task',
  // I-2382: a session's state is the Agents view's, which shows it from the session; never a task.
  [NOTIFICATION_TYPES.AGENT_WAITING]: 'agents',
  [NOTIFICATION_TYPES.AGENT_IDLE]: 'agents',
  [NOTIFICATION_TYPES.AGENT_ERROR]: 'agents',
  [NOTIFICATION_TYPES.AGENT_BLOCKED]: 'task',
  [NOTIFICATION_TYPES.DELIVERY_FAULT]: 'task',
  [NOTIFICATION_TYPES.CREDENTIAL_REVOKED_AFTER_REUSE]: 'task',
  // I-2072: about the reader's own account, and the only place they hear of it (no mail is sent).
  [NOTIFICATION_TYPES.PASSWORD_RESET_LIMITED]: 'task',
  [NOTIFICATION_TYPES.NAMESPACE_HANDLE_REPLACED]: 'task',
  [NOTIFICATION_TYPES.PLAN_LIMIT_WARNING]: 'task',
  [NOTIFICATION_TYPES.AGENT_CREATION_DECIDED]: 'task',
  [NOTIFICATION_TYPES.STATE_CHANGED]: 'record',
  [NOTIFICATION_TYPES.MOVE_DECLINED]: 'record',
  // F-0787 (AC-AQ32, AC-AQ33): a person's own request's outcome is a *For you* task; an agent's
  // outcome, and its owner's copy, stay the record (`accessOutcomeForAPerson`).
  [NOTIFICATION_TYPES.ACCESS_REQUEST_DECIDED]: 'task',
};

/**
 * Kinds a list already shows from their own source, so their notice is neither drawn nor counted:
 * a waiting access request is a row of Decisions for me (AC-SV18), a held move a hold row of Tasks
 * for me (GV-2e). Drawing the notice too would list one wait twice and count it twice.
 *
 * I-2382 (AC-NB13): an item made the reader's, and a reply on it, are the item's own row of Tasks or
 * Decisions for me while the item is theirs and open; once it is closed or somebody else's, nothing
 * waits on them. A reply on an item they only watch is the record. Jay's badge counted 72 of these.
 */
export const NOTICES_SHOWN_BY_THEIR_SOURCE: readonly NotificationType[] = [
  NOTIFICATION_TYPES.ACCESS_REQUESTED,
  NOTIFICATION_TYPES.MOVE_REQUESTED,
  NOTIFICATION_TYPES.RESPONSIBLE_CHANGED,
  NOTIFICATION_TYPES.COMMENT_ADDED,
  // F-0815: the urgent task is its own row in Tasks for me, marked Urgent (TF-36).
  NOTIFICATION_TYPES.TASK_URGENT,
];

/**
 * Kinds that are a decision only while unanswered: the notice settles from critical to info once
 * anyone answers, and is then not drawn (AC-NB9).
 */
export const NOTICES_DRAWN_WHILE_CRITICAL: readonly NotificationType[] = [
  NOTIFICATION_TYPES.AGENT_CREATION_REQUESTED,
];

/**
 * F-0787 (AC-AQ32, AC-AQ33): whether an access request's outcome was told to the person who asked for
 * themself. Only that one is drawn; an agent's outcome, to the agent or its owner, is the record:
 * the agent's own status and the access trail say it.
 */
export function accessOutcomeForAPerson(notice: Pick<INoticeFacts, 'type' | 'data'>): boolean {
  return (
    notice.type === NOTIFICATION_TYPES.ACCESS_REQUEST_DECIDED &&
    (notice.data as Record<string, unknown> | null | undefined)?.requester === 'person'
  );
}

/** Where a stored kind goes; a kind this build does not know is the record, never drawn. */
export function notificationDestination(type: string): NoticeDestinationType {
  return (NOTIFICATION_DESTINATIONS as Record<string, NoticeDestinationType>)[type] ?? 'record';
}

/** The kinds a list draws as notices: its place's kinds, less those shown by their own source. */
export function noticeKindsDrawnIn(place: NoticePlaceType): NotificationType[] {
  return (Object.keys(NOTIFICATION_DESTINATIONS) as NotificationType[]).filter(
    (type) =>
      NOTIFICATION_DESTINATIONS[type] === place && !NOTICES_SHOWN_BY_THEIR_SOURCE.includes(type),
  );
}

/** Every kind a place draws: what the web asks the server for, and nothing else (AC-NB11). */
export function drawnNoticeKinds(): NotificationType[] {
  return NOTICE_PLACES.flatMap((place) => noticeKindsDrawnIn(place));
}

/** The item type whose notices are Decisions for me's (I-2382, AC-NB14). */
const DECISION_ITEM_TYPE_KEY = 'decision';

/**
 * What the place and scope rules read of a notice. The web reads a listed notice, the server a row
 * it selected: an item's work area is `project.id` in one and `projectId` in the other.
 */
export interface INoticeFacts {
  type: string;
  severity?: string | null;
  data?: unknown;
  workItem?: {
    projectId?: string | null;
    project?: { id: string } | null;
    itemType?: { key?: string | null } | null;
  } | null;
}

/**
 * The place this notice is drawn in now, or null where none draws it: a record kind, a kind shown by
 * its own source, or a kind that is a decision only while unanswered once it is answered (AC-NB9).
 * A task kind about a decision is Decisions for me's, never a row of Tasks for me (I-2382, AC-NB14).
 */
export function noticePlace(notice: INoticeFacts): NoticePlaceType | null {
  const destination = notificationDestination(notice.type);
  if (destination === 'record') return null;
  if ((NOTICES_SHOWN_BY_THEIR_SOURCE as string[]).includes(notice.type)) return null;
  if (
    notice.type === NOTIFICATION_TYPES.ACCESS_REQUEST_DECIDED &&
    !accessOutcomeForAPerson(notice)
  ) {
    return null;
  }
  if (
    (NOTICES_DRAWN_WHILE_CRITICAL as string[]).includes(notice.type) &&
    !isCriticalNotification(notice)
  ) {
    return null;
  }
  if (destination === 'task' && notice.workItem?.itemType?.key === DECISION_ITEM_TYPE_KEY) {
    return 'decision';
  }
  return destination;
}

/**
 * Whether this notice is drawn in that place now: its kind is drawn there, a kind that is a decision
 * only while unanswered is still critical, and an item notice about a decision is in Decisions.
 */
export function noticeIsDrawnIn(notice: INoticeFacts, place: NoticePlaceType): boolean {
  return noticePlace(notice) === place;
}

/** The work area a notice is about: its item's, or an access end's; null for one about the person. */
export function noticeWorkAreaId(notice: INoticeFacts): string | null {
  const itemWorkArea = notice.workItem?.project?.id ?? notice.workItem?.projectId;
  if (itemWorkArea) return itemWorkArea;
  if (
    notice.type === NOTIFICATION_TYPES.ACCESS_ENDING ||
    notice.type === NOTIFICATION_TYPES.ACCESS_ENDED
  ) {
    const workAreaId = (notice.data as Record<string, unknown> | null | undefined)?.workAreaId;
    return typeof workAreaId === 'string' && workAreaId ? workAreaId : null;
  }
  return null;
}

/**
 * How many waiting notices one place holds (I-2382, AC-NB17): those about the person, which are in
 * every scope, and those of each work area. A badge adds the work areas in its scope.
 */
export interface IWaitingNoticeCount {
  person: number;
  byWorkArea: Record<string, number>;
}

export type WaitingNoticeCountsType = Record<NoticePlaceType, IWaitingNoticeCount>;

/** The server's `waiting` answer: every waiting notice it selected, counted by place and work area. */
export function countWaitingNotices(notices: readonly INoticeFacts[]): WaitingNoticeCountsType {
  const counts = Object.fromEntries(
    NOTICE_PLACES.map((place) => [place, { person: 0, byWorkArea: {} }]),
  ) as WaitingNoticeCountsType;
  for (const notice of notices) {
    const place = noticePlace(notice);
    if (!place) continue;
    const workAreaId = noticeWorkAreaId(notice);
    if (workAreaId === null) counts[place].person += 1;
    else counts[place].byWorkArea[workAreaId] = (counts[place].byWorkArea[workAreaId] ?? 0) + 1;
  }
  return counts;
}

/** One place's waiting notices in a scope: the person's, and those of the work areas it takes in. */
export function waitingNoticesInScope(
  count: IWaitingNoticeCount,
  inScope: (workAreaId: string) => boolean,
): number {
  return Object.entries(count.byWorkArea).reduce(
    (sum, [workAreaId, n]) => (inScope(workAreaId) ? sum + n : sum),
    count.person,
  );
}
